import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { getCookie } from 'libraries/cookie'
import { CartItem, ToggleGiftPackagePayload } from './apiCartSliceTypes'

const baseQuery = fetchBaseQuery({
  baseUrl: window.config.API_URL,
  prepareHeaders: headers => {
    const storeId = window.config.STORE_ID?.toString() || '';
    if (storeId) {
      headers.set('X-BSTORE-ID', storeId);
    }
    const token = getCookie('token')
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return headers
  },
})

// Custom base query to handle errors properly
const baseQueryWithErrorHandling: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions)

  // Log errors for debugging
  if (result.error) {
    console.log('Cart API Error:', {
      status: result.error.status,
      data: result.error.data,
    })
  }

  return result
}

export const cartApi = createApi({
  reducerPath: 'cartApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Cart'],
  endpoints: builder => ({
    getCartItems: builder.query<any, { merchant_id: number; per_page?: number; page?: number }>({
      query: (param: any) => ({
        url: 'v4/cart/merchant-items',
        params: {
          per_page: param.per_page,
          page: param.page,
          merchant_id: param.merchant_id,
        },
      }),
      providesTags: ['Cart'],
    }),

    postCartItem: builder.mutation<any, any>({
      query: payload => ({
        url: 'v4/cart',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Cart'],
    }),

    toggleGiftPackage: builder.mutation<void, ToggleGiftPackagePayload>({
      query: payload => ({
        url: '/suppliers/v4/cart/gift-package',
        method: 'PUT',
        body: payload,
      }),
    }),
    deleteOneCart: builder.mutation<void, { cart_item_id: number }>({
      query: cart_item_id => ({
        url: `/v4/cart/${cart_item_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Cart'],
    }),
    updateOptionById: builder.mutation<CartItem, { cart_item_option_value_id: number; quantity: number }>({
      query: ({ cart_item_option_value_id, quantity }) => ({
        url: `/v4/cart/${cart_item_option_value_id}`,
        method: 'PUT',
        body: { quantity },
      }),
      invalidatesTags: ['Cart'],
    }),
  }),
})

export const {
  useGetCartItemsQuery,
  usePostCartItemMutation,
  useToggleGiftPackageMutation,
  useDeleteOneCartMutation,
  useUpdateOptionByIdMutation,
} = cartApi
